import type { PaywallCheckoutPreview } from "@circle-react/types";

import { t } from "@/i18n-js/instance";
import { isTrialThenValue } from "@/react/helpers/paywallPriceHelpers";

import type { CheckoutPreviewEntryProps } from "../../../components/PreviewEntry";
import type { ComponentLineProps } from "../../FirstPaymentPreview/types";
import { buildRemainingPayments } from "../../RemainingPaymentsPreview/helpers";
import { RawLines } from "../RawLines";
import { getNumberOfInstallments } from "../variants/helpers";

const discount = (
  preview: PaywallCheckoutPreview,
): Partial<CheckoutPreviewEntryProps> => ({
  tooltip: undefined,
  hide: (preview.first_payment?.discount || 0) === 0,
  value: preview.first_payment.formatted_discount,
});

const discountForFuturePayments = (
  preview: PaywallCheckoutPreview,
): Partial<CheckoutPreviewEntryProps> => ({
  tooltip: undefined,
  hide: (preview.future_payments?.discount || 0) === 0,
  value: preview.future_payments?.formatted_discount,
});

const oneInstallmentsStrategies = {
  trial_then_value: discount,
  trial_then_upfront_then_value: discountForFuturePayments,
  upfront_then_trial_then_value: discountForFuturePayments,
  upfront_then_value: discountForFuturePayments,
  value: () => ({ hide: true }),
};

const buildOptions = (
  preview: PaywallCheckoutPreview,
): Partial<CheckoutPreviewEntryProps> => {
  if (!preview.paywall_coupon) {
    return { hide: true };
  }

  if (getNumberOfInstallments(preview) === 1) {
    return oneInstallmentsStrategies[preview.preview_scenario](preview);
  }

  const remainingPayments = buildRemainingPayments(preview);
  const tooltipText =
    (remainingPayments?.coupons_applied_quantity || 0) > 0
      ? t(
          [
            "paywall_checkout.future_payments.discount_phrase_v2",
            preview?.paywall_coupon?.duration,
          ],
          {
            count: remainingPayments?.coupons_applied_quantity,
          },
        )
      : undefined;

  if (isTrialThenValue(preview.preview_scenario)) {
    return {
      hide: preview.first_payment.discount === 0,
      value: preview.first_payment.formatted_discount,
      tooltip: tooltipText,
    };
  }

  return {
    hide: (preview.future_payments?.discount || 0) === 0,
    tooltip: tooltipText,
  };
};

export const DiscountLine = ({
  preview,
}: Pick<ComponentLineProps, "preview">) => {
  const options = buildOptions(preview);
  return <RawLines.DiscountLine preview={preview} options={options} />;
};
